import React from 'react';
import {
   Box,
   Button,
   CircularProgress,
   Dialog,
   DialogActions,
   DialogContent,
   DialogTitle,
   Divider,
   FormControlLabel,
   IconButton,
   List,
   ListItem,
   ListItemText,
   Switch,
   TextField,
   Typography
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import {GroupFragment, useGroupCreateMutation, useGroupsQuery, useGroupUpdateMutation} from "../../../Queries";

interface MasterDataGroupsProps {
   open: boolean;
   onClose: () => void;
   brandHouseId: string;
}

export const MasterDataGroups: React.FC<MasterDataGroupsProps> = ({open, onClose, brandHouseId}) => {
   // Queries and mutations
   const {data, loading, refetch} = useGroupsQuery({
      variables: {brandHouseId},
      skip: !open
   });

   const [createGroup, {loading: createLoading}] = useGroupCreateMutation();
   const [updateGroup, {loading: updateLoading}] = useGroupUpdateMutation();

   const handleToggleProperty = async (group: GroupFragment, property: 'active' | 'activePotentials' | 'activePromotions') => {
      const updateInput = {
         [property]: !group[property]
      };

      await updateGroup({
         variables: {
            groupId: group._id,
            input: updateInput
         }
      });
      refetch();
   };

   const handleCreateGroup = async (event: React.FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      const formData = new FormData(event.currentTarget);
      const newGroupName = formData.get('newGroupName') as string;

      if (newGroupName?.trim()) {
         await createGroup({
            variables: {
               input: {
                  name: newGroupName,
                  brandHouseId,
                  active: true,
                  activePotentials: true,
                  activePromotions: true
               }
            }
         });
         refetch();
         // Reset the form
         event.currentTarget.reset();
      }
   };

   const handleUpdateGroup = async (event: React.FormEvent<HTMLFormElement>, groupId: string) => {
      event.preventDefault();
      const formData = new FormData(event.currentTarget);
      const updatedName = formData.get('groupName') as string;
      const active = formData.has('active');
      const activePotentials = formData.has('activePotentials');
      const activePromotions = formData.has('activePromotions');

      if (updatedName?.trim()) {
         await updateGroup({
            variables: {
               groupId,
               input: {
                  name: updatedName,
                  active,
                  activePotentials,
                  activePromotions
               }
            }
         });
         refetch();
      }
   };

   return (
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
         <DialogTitle>
            <Box display="flex" justifyContent="space-between" alignItems="center">
               <Typography variant="h6">Product Groups</Typography>
               <IconButton edge="end" color="inherit" onClick={onClose} aria-label="close">
                  <CloseIcon/>
               </IconButton>
            </Box>
         </DialogTitle>
         <DialogContent>
            {loading ? (
               <Box display="flex" justifyContent="center" p={2}>
                  <CircularProgress/>
               </Box>
            ) : (
               <>
                  <List>
                     {data?.BrandProductGroups?.map((group: GroupFragment) => (
                        <React.Fragment key={group._id}>
                           <ListItem
                              alignItems="flex-start"
                           >
                              <Box sx={{ width: '100%' }}>
                                 <ListItemText primary={group.name} />
                                 <Box sx={{ display: 'flex', mt: 1 }}>
                                    <FormControlLabel
                                       control={
                                          <Switch
                                             size="small"
                                             checked={group.active}
                                             onChange={() => handleToggleProperty(group, 'active')}
                                             disabled={updateLoading}
                                          />
                                       }
                                       label="Active"
                                    />
                                    <FormControlLabel
                                       control={
                                          <Switch
                                             size="small"
                                             checked={group.activePotentials||false}
                                             onChange={() => handleToggleProperty(group, 'activePotentials')}
                                             disabled={updateLoading}
                                          />
                                       }
                                       label="Active Potentials"
                                    />
                                    <FormControlLabel
                                       control={
                                          <Switch
                                             size="small"
                                             checked={group.activePromotions||false}
                                             onChange={() => handleToggleProperty(group, 'activePromotions')}
                                             disabled={updateLoading}
                                          />
                                       }
                                       label="Active Promotions"
                                    />
                                 </Box>
                              </Box>
                              <IconButton
                                 edge="end"
                                 aria-label="edit"
                                 onClick={() => {
                                    const editForm = document.getElementById(`edit-form-${group._id}`) as HTMLFormElement;
                                    if (editForm) {
                                       editForm.style.display = editForm.style.display === 'none' ? 'block' : 'none';
                                    }
                                 }}
                              >
                                 <EditIcon/>
                              </IconButton>
                           </ListItem>
                           <Box
                              component="form"
                              id={`edit-form-${group._id}`}
                              sx={{ display: 'none', p: 2, ml: 2, border: '1px dashed rgba(0, 0, 0, 0.23)', borderRadius: 1 }}
                              onSubmit={(e) => handleUpdateGroup(e, group._id)}
                           >
                              <TextField
                                 name="groupName"
                                 defaultValue={group.name}
                                 fullWidth
                                 required
                                 size="small"
                                 label="Group Name"
                                 variant="outlined"
                                 margin="normal"
                              />
                              <Box sx={{ mt: 2 }}>
                                 <FormControlLabel
                                    control={
                                       <Switch
                                          name="active"
                                          defaultChecked={group.active}
                                       />
                                    }
                                    label="Active"
                                 />
                                 <FormControlLabel
                                    control={
                                       <Switch
                                          name="activePotentials"
                                          defaultChecked={group.activePotentials||false}
                                       />
                                    }
                                    label="Active Potentials"
                                 />
                                 <FormControlLabel
                                    control={
                                       <Switch
                                          name="activePromotions"
                                          defaultChecked={group.activePromotions||false}
                                       />
                                    }
                                    label="Active Promotions"
                                 />
                              </Box>
                              <Box display="flex" justifyContent="flex-end" mt={1}>
                                 <Button
                                    type="button"
                                    variant="text"
                                    onClick={() => {
                                       const editForm = document.getElementById(`edit-form-${group._id}`) as HTMLFormElement;
                                       if (editForm) {
                                          editForm.style.display = 'none';
                                          editForm.reset();
                                       }
                                    }}
                                 >
                                    Cancel
                                 </Button>
                                 <Button
                                    type="submit"
                                    variant="contained"
                                    disabled={updateLoading}
                                    startIcon={updateLoading ? <CircularProgress size={20}/> : <SaveIcon />}
                                    sx={{ml: 1}}
                                 >
                                    Save
                                 </Button>
                              </Box>
                           </Box>
                           <Divider />
                        </React.Fragment>
                     ))}
                  </List>

                  <Box
                     component="form"
                     onSubmit={handleCreateGroup}
                     sx={{ mt: 2, p: 2, border: '1px dashed rgba(0, 0, 0, 0.23)', borderRadius: 1 }}
                  >
                     <TextField
                        name="newGroupName"
                        fullWidth
                        required
                        label="New Group Name"
                        variant="outlined"
                        size="small"
                     />
                     <Box display="flex" justifyContent="flex-end" mt={1}>
                        <Button
                           type="reset"
                           variant="text"
                        >
                           Clear
                        </Button>
                        <Button
                           type="submit"
                           variant="contained"
                           disabled={createLoading}
                           startIcon={createLoading ? <CircularProgress size={20}/> : <AddIcon />}
                           sx={{ml: 1}}
                        >
                           Save
                        </Button>
                     </Box>
                  </Box>
               </>
            )}
         </DialogContent>
         <DialogActions>
            <Button onClick={onClose} color="primary">
               Close
            </Button>
         </DialogActions>
      </Dialog>
   );
};